// Infographic Scene guide — the AI scene-builder modal (InfographicSceneModal, three tabs).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const infographicSceneGuide = {
  id: "infographic-scene",
  icon: "dashboard",
  keywords: [
    "infographic",
    "infografik",
    "infographic scene",
    "infografik sahne",
    "infographic image",
    "infografik görsel",
    "generate scene",
    "sahne oluştur",
    "generate from narration",
    "anlatımdan oluştur",
    "style",
    "stil",
    "organization style",
    "organizasyon stili",
    "layout",
    "düzen",
    "aidea engine",
    "engine",
    "motor",
    "characters",
    "karakter",
    "reference images",
    "referans görseller",
    "approve",
    "onayla",
    "credits",
    "kredi",
    "infografía",
    "escena infográfica",
    "imagen infográfica",
    "generar escena",
    "generar desde narración",
    "estilo",
    "estilo de organización",
    "motor",
    "personajes",
    "imágenes de referencia",
    "aprobar",
    "créditos",
  ],
  content: {
    en: {
      feature: "AI",
      title: "Infographic Scene",
      sections: [
        {
          id: "overview",
          title: "Overview",
          videoUrl: null,
          text:
            "Click **Infographic Scene** in the AI tools group of the top navbar (on narrower screens, inside the **AI** menu) to have AI design visuals for you — layout, images and text together — instead of building them element by element. The Aidea Assistant can also open this window for you, already filled in.\n\n" +
            "The window has three tabs that share the same provider, engine and style settings:\n\n" +
            "- **Generate New Scene** — write a narration and get a brand-new, fully styled scene inserted after the selected one.\n" +
            "- **Generate Infographic Image** — turn a short text into a single infographic image added to the current scene.\n" +
            "- **Generate From Narration** — reuse the current scene's narration to regenerate its images (replaces the existing ones).",
        },
        {
          id: "generate",
          title: "Generating a scene",
          videoUrl: null,
          text:
            "On the **Generate New Scene** tab, write or paste your content in the **Narration Text** box — this is what the AI turns into the scene. **Fetch Scene Text** fills it with the current scene's narration.\n\n" +
            "Then configure the scene:\n\n" +
            "- **Audio Narration** — choose the narrator voice for the new scene (filter by provider, language and gender, and play samples).\n" +
            "- **AI Provider** — leave on **Auto** to let Aidea pick, or switch to **Custom** to choose one yourself.\n" +
            "- **Aidea Engine** — choose which visual engine generates the visuals (see below).\n" +
            "- **Style** — pick a style card, **Custom Style Used in This Course** (when the course has one), or **Custom** to describe your own. If your organization has styles, an **Organization Style** row appears above.\n" +
            "- **Reference Images** — optionally add up to three images (upload, drag and drop, or **Select from Library**) to guide the look; you can tag each one.\n\n" +
            "Click **Credits** for an estimated cost and your balance, then **Generate**.",
        },
        {
          id: "approve-result",
          title: "Approving the result",
          videoUrl: null,
          text:
            "Nothing is added until you approve it. After **Generate**, the window shows a progress spinner and then the **Generated images** for review.\n\n" +
            "- **Approve** adds the result — on the **Generate New Scene** tab, as a new scene right after the selected one — and closes the window. Every element it creates (text, images, shapes) is fully editable.\n" +
            "- **Discard** throws the result away and takes you back to the form.\n" +
            "- If you close the window while it's still generating, the result waits in the job widget as **Waiting Approval**, and you can approve it there.",
        },
        {
          id: "image-and-narration",
          title: "Infographic image and generating from narration",
          videoUrl: null,
          text:
            "- **Generate Infographic Image** — write the text in **Image Text** (or use **Fetch Scene Text**), pick provider, engine and style, then **Generate**. After you approve it, one infographic image is added to the current scene.\n" +
            "- **Generate From Narration** — shows the current scene's narration text (read-only) and its audio player. **Generate** creates new images from that narration; after you approve them, they replace the scene's existing visuals. Use an empty scene if you want to keep the current visuals.",
        },
        {
          id: "aidea-engine",
          title: "Choosing an Aidea Engine",
          videoUrl: null,
          text:
            "Each engine's description is shown under its name in the **Aidea Engine** list.\n\n" +
            "- **Aidea Engine 1** — consistent visuals in a standard style.\n" +
            "- **Aidea Engine 2** — visually rich, varied styles across slides, with more visuals per scene.\n" +
            "- **Aidea Engine 3** — clean style with minimal text; the visuals carry the content.\n" +
            "- **Aidea Engine 5** — focused style with fewer, bolder elements and short labels.\n" +
            "- **Aidea Engine 7 (Characters)** and **Aidea Engine 8 (Characters, no text)** — reuse the course's fixed cast of characters. They only appear for trainings created with a character cast; with them the provider is fixed and **Organization Style** is hidden.",
        },
      ],
    },
    tr: {
      feature: "Yapay Zeka",
      title: "İnfografik Sahne",
      sections: [
        {
          id: "overview",
          title: "Genel Bakış",
          videoUrl: null,
          text:
            "Görselleri öğe öğe kendiniz kurmak yerine düzen, görseller ve metni yapay zekânın birlikte tasarlaması için üst gezinme çubuğundaki yapay zeka araçları grubunda **İnfografik Sahne**'ye tıklayın (dar ekranlarda **AI** menüsünün içindedir). Aidea Asistan da bu pencereyi sizin için önceden doldurulmuş olarak açabilir.\n\n" +
            "Pencerede, aynı sağlayıcı, motor ve stil ayarlarını paylaşan üç sekme vardır:\n\n" +
            "- **Yeni Sahne Oluştur** — bir anlatım metni yazın, seçili sahnenin ardına eklenecek, tamamen hazır ve stillendirilmiş yepyeni bir sahne alın.\n" +
            "- **İnfografik Görsel Oluştur** — kısa bir metni, geçerli sahneye eklenecek tek bir infografik görsele dönüştürün.\n" +
            "- **Anlatımdan Oluştur** — geçerli sahnenin anlatımını kullanarak görsellerini yeniden oluşturun (var olanların yerini alır).",
        },
        {
          id: "generate",
          title: "Sahne oluşturma",
          videoUrl: null,
          text:
            "**Yeni Sahne Oluştur** sekmesinde, yapay zekânın sahneye dönüştüreceği metni **Anlatım Metni** kutusuna yazın veya yapıştırın. **Sahne Metnini Getir**, kutuyu geçerli sahnenin anlatımıyla doldurur.\n\n" +
            "Ardından sahneyi ayarlayın:\n\n" +
            "- **Anlatım Sesi** — yeni sahnenin anlatıcı sesini seçin (sağlayıcıya, dile ve cinsiyete göre filtreleyip örnekleri dinleyebilirsiniz).\n" +
            "- **AI Sağlayıcısı** — Aidea'nın seçmesi için **Otomatik**'te bırakın ya da kendiniz seçmek için **Özel**'e geçin.\n" +
            "- **Aidea Engine** — görselleri hangi görsel motorun üreteceğini seçin (aşağıya bakın).\n" +
            "- **Stil** — bir stil kartı, **Bu Kursta Kullanılan Özel Stil** (kursta varsa) ya da kendi tanımınızı yazmak için **Özel** seçin. Kurumunuzun stilleri varsa üstte bir **Organizasyon Stili** bölümü de görünür.\n" +
            "- **Referans Görseller** — görünümü yönlendirmek için isteğe bağlı olarak en fazla üç görsel ekleyin (yükleyin, sürükleyip bırakın veya **Kütüphaneden Seç**); her birine etiket ekleyebilirsiniz.\n\n" +
            "Tahmini maliyeti ve bakiyenizi görmek için **Kredi** düğmesine, ardından **Oluştur**'a tıklayın.",
        },
        {
          id: "approve-result",
          title: "Sonucu onaylama",
          videoUrl: null,
          text:
            "Siz onaylamadan hiçbir şey eklenmez. **Oluştur**'dan sonra pencerede önce bir ilerleme göstergesi, ardından incelemeniz için **Üretilen görseller** görünür.\n\n" +
            "- **Onayla**, sonucu ekler — **Yeni Sahne Oluştur** sekmesinde seçili sahnenin hemen ardına yeni bir sahne olarak — ve pencereyi kapatır. Oluşturulan her öğe (metin, görsel, şekil) tamamen düzenlenebilir.\n" +
            "- **Vazgeç**, sonucu atar ve sizi forma geri götürür.\n" +
            "- Üretim sürerken pencereyi kapatırsanız sonuç iş kuyruğu widget'ında **Onay Bekliyor** olarak bekler; onu oradan onaylayabilirsiniz.",
        },
        {
          id: "image-and-narration",
          title: "İnfografik görsel ve anlatımdan oluşturma",
          videoUrl: null,
          text:
            "- **İnfografik Görsel Oluştur** — metni **Görsel Metni** kutusuna yazın (veya **Sahne Metnini Getir**'i kullanın), sağlayıcıyı, motoru ve stili seçip **Oluştur**'a tıklayın. Onayladığınızda geçerli sahneye tek bir infografik görsel eklenir.\n" +
            "- **Anlatımdan Oluştur** — geçerli sahnenin anlatım metnini (salt okunur) ve ses oynatıcısını gösterir. **Oluştur**, bu anlatımdan yeni görseller üretir; onayladığınızda sahnedeki mevcut görsellerin yerini alır. Mevcut görselleri korumak istiyorsanız boş bir sahnede çalışın.",
        },
        {
          id: "aidea-engine",
          title: "Aidea Engine seçimi",
          videoUrl: null,
          text:
            "Her motorun açıklaması, **Aidea Engine** listesinde adının altında görünür.\n\n" +
            "- **Aidea Engine 1** — standart bir stilde, birbiriyle uyumlu görseller.\n" +
            "- **Aidea Engine 2** — slaytlar arasında değişen stillerle görsel açıdan zengin, sahne başına daha fazla görsel.\n" +
            "- **Aidea Engine 3** — az yazılı, içeriği büyük ölçüde görsellerle anlatan sade bir stil.\n" +
            "- **Aidea Engine 5** — daha az sayıda, iri öğeler ve kısa etiketlerle odaklı bir stil.\n" +
            "- **Aidea Engine 7 (Karakterli)** ve **Aidea Engine 8 (Karakterli, yazısız)** — eğitimin sabit karakter kadrosunu kullanır. Yalnızca karakter kadrosuyla oluşturulmuş eğitimlerde görünür; bu motorlarda sağlayıcı sabittir ve **Organizasyon Stili** gizlenir.",
        },
      ],
    },
    es: {
      feature: "IA",
      title: "Escena infográfica",
      sections: [
        {
          id: "overview",
          title: "Visión general",
          videoUrl: null,
          text:
            "Haz clic en **Escena Infográfica** en el grupo de herramientas de IA de la barra de navegación superior (en pantallas más estrechas, dentro del menú **IA**) para que la IA diseñe los elementos visuales por ti (maquetación, imágenes y texto a la vez) en lugar de construirlos elemento a elemento. El Asistente de Aidea también puede abrir esta ventana por ti, ya rellenada.\n\n" +
            "La ventana tiene tres pestañas que comparten los mismos ajustes de proveedor, motor y estilo:\n\n" +
            "- **Generar Nueva Escena**: escribe una narración y obtén una escena completamente nueva y con estilo, que se inserta después de la escena seleccionada.\n" +
            "- **Generar Imagen Infográfica**: convierte un texto breve en una sola imagen infográfica que se añade a la escena actual.\n" +
            "- **Generar desde Narración**: reutiliza la narración de la escena actual para volver a generar sus imágenes (sustituye las existentes).",
        },
        {
          id: "generate",
          title: "Generar una escena",
          videoUrl: null,
          text:
            "En la pestaña **Generar Nueva Escena**, escribe o pega tu contenido en el cuadro **Texto de Narración**: es lo que la IA convierte en la escena. **Obtener texto de la escena** lo rellena con la narración de la escena actual.\n\n" +
            "Después, configura la escena:\n\n" +
            "- **Narración de audio**: elige la voz del narrador para la nueva escena (filtra por proveedor, idioma y género, y escucha muestras).\n" +
            "- **Proveedor de IA**: déjalo en **Automático** para que Aidea elija, o cambia a **Personalizado** para elegirlo tú.\n" +
            "- **Aidea Engine**: elige qué motor visual genera las imágenes (consulta más abajo).\n" +
            "- **Estilo**: elige una tarjeta de estilo, **Estilo Personalizado Usado en Este Curso** (si el curso tiene uno) o **Personalizado** para describir el tuyo. Si tu organización tiene estilos, aparece encima una fila **Estilo de Organización**.\n" +
            "- **Imágenes de Referencia**: si quieres, añade hasta tres imágenes (súbelas, arrástralas y suéltalas o usa **Seleccionar de la Biblioteca**) para orientar el aspecto; puedes etiquetar cada una.\n\n" +
            "Haz clic en **Créditos** para ver el coste estimado y tu saldo y, después, en **Generar**.",
        },
        {
          id: "approve-result",
          title: "Aprobar el resultado",
          videoUrl: null,
          text:
            "No se añade nada hasta que lo apruebas. Después de **Generar**, la ventana muestra un indicador de progreso y luego las **Imágenes generadas** para que las revises.\n\n" +
            "- **Aprobar** añade el resultado (en la pestaña **Generar Nueva Escena**, como una escena nueva justo después de la seleccionada) y cierra la ventana. Todos los elementos que crea (texto, imágenes, formas) son totalmente editables.\n" +
            "- **Descartar** desecha el resultado y te devuelve al formulario.\n" +
            "- Si cierras la ventana mientras aún se está generando, el resultado espera en el widget de trabajos como **Pendiente de aprobación**, y puedes aprobarlo allí.",
        },
        {
          id: "image-and-narration",
          title: "Imagen infográfica y generación desde la narración",
          videoUrl: null,
          text:
            "- **Generar Imagen Infográfica**: escribe el texto en **Texto de Imagen** (o usa **Obtener texto de la escena**), elige proveedor, motor y estilo y haz clic en **Generar**. Cuando lo apruebas, se añade una imagen infográfica a la escena actual.\n" +
            "- **Generar desde Narración**: muestra el texto de narración de la escena actual (solo lectura) y su reproductor de audio. **Generar** crea imágenes nuevas a partir de esa narración; cuando las apruebas, sustituyen a los elementos visuales existentes de la escena. Usa una escena vacía si quieres conservar los elementos visuales actuales.",
        },
        {
          id: "aidea-engine",
          title: "Elegir un Aidea Engine",
          videoUrl: null,
          text:
            "La descripción de cada motor aparece bajo su nombre en la lista **Aidea Engine**.\n\n" +
            "- **Aidea Engine 1**: imágenes coherentes en un estilo estándar.\n" +
            "- **Aidea Engine 2**: visualmente rico, con estilos variados entre diapositivas y más imágenes por escena.\n" +
            "- **Aidea Engine 3**: estilo limpio con un mínimo de texto; las imágenes transmiten el contenido.\n" +
            "- **Aidea Engine 5**: estilo enfocado, con menos elementos pero más grandes y etiquetas breves.\n" +
            "- **Aidea Engine 7 (Personajes)** y **Aidea Engine 8 (Personajes, sin texto)**: reutilizan el reparto fijo de personajes del curso. Solo aparecen en formaciones creadas con un reparto de personajes; con ellos el proveedor es fijo y **Estilo de Organización** se oculta.",
        },
      ],
    },
  },
};

export default infographicSceneGuide;
